import { base64ToByteArray } from './base64TtoByteArray';

export function formatUpdateA3DocUsersOnboardingJson({
    token,
    documentData,
    users
}) {
    const getFK = (customerId, code) => `${customerId}-${code.padStart(5, '0')}`;
    const attr800 = documentData.attributes.find(attr => attr.id === "800");
    const code800 = attr800 ? attr800.value.padStart(5, '0') : "";

    // Agrupar usuarios por `workplaceCode` para manejar centros únicos
    const uniqueWorkplaceCodes = Array.from(new Set(users.map(user => user.workplaceCode)));

    // Extraer centros (ID 801) y trabajadores (ID 802) existentes en documentData.attributes
    const existingCenters = documentData.attributes.filter(attr => attr.id === "801");
    const existingWorkers = documentData.attributes.filter(attr => attr.id === "802");

    const transformedExistingCenters = existingCenters.map(center => ({
        "ID": "801",
        "DetailsMainField": "Código",
        "Items": [
            { "Name": "Código", "Value": center.value },
            { "Name": "Nombre", "Value": center.description || "" },
            { "Name": "Empresa", "Value": documentData.customerName },
            { "Name": "Cliente", "Value": documentData.customerName },
            { "Name": "FK", "Value": documentData.customerId }
        ]
    }));

    // Transformar los trabajadores existentes al formato correcto
    const transformedExistingWorkers = existingWorkers.map(worker => ({
        "ID": "802",
        "DetailsMainField": "Trabajador",
        "Items": [
            { "Name": "NIF", "Value": worker.value },
            { "Name": "Nombre", "Value": worker.description.split(',')[1].trim() || "" },  // Asume que el formato es "APELLIDO, NOMBRE"
            { "Name": "Centro", "Value": worker.workplaceCode ? worker.workplaceCode.padStart(5, '0') : "" },
            { "Name": "Empresa", "Value": documentData.customerName },
            { "Name": "FK", "Value": getFK(documentData.customerId, code800) }
        ]
    }));

    // Crear una lista para los nuevos centros y trabajadores que no existan
    const newCenters = uniqueWorkplaceCodes.map(workplaceCode => {
        const userForWorkplace = users.find(user => user.workplaceCode === workplaceCode);

        // Verificar si ya existe el centro
        const existsCentro = existingCenters.some(attr => attr.value === workplaceCode.padStart(5, '0'));
        if (!existsCentro) {
            return {
                "ID": "801",
                "DetailsMainField": "Código",
                "Items": [
                    { "Name": "Código", "Value": workplaceCode.padStart(5, '0') },
                    { "Name": "Nombre", "Value": userForWorkplace?.workplaceName || "" },
                    { "Name": "Empresa", "Value": documentData.customerName },
                    { "Name": "Cliente", "Value": documentData.customerName },
                    { "Name": "FK", "Value": documentData.customerId }
                ]
            };
        }
    }).filter(Boolean); // Filtrar los nulos

    const newWorkers = users.map(user => {
        // Verificar si ya existe el trabajador
        const existsWorker = existingWorkers.some(attr => attr.value === user.nif);
        if (!existsWorker) {
            return {
                "ID": "802",
                "DetailsMainField": "Trabajador",
                "Items": [
                    { "Name": "NIF", "Value": user.nif || "" },
                    { "Name": "Nombre", "Value": `${user.surnames || ""}, ${user.name || ""}`.trim() || "" },
                    { "Name": "Centro", "Value": user.workplaceCode.padStart(5, '0') },
                    { "Name": "Empresa", "Value": documentData.customerName },
                    { "Name": "FK", "Value": getFK(documentData.customerId, code800) }
                ]
            };
        }
    }).filter(Boolean); // Filtrar los nulos



    // Combinar los atributos existentes con los nuevos sin duplicar
    const documentAttributeValues = [
        {
            "ID": "1",
            "DetailsMainField": "Identificador",
            "Items": [
                {
                    "Name": "Identificador",
                    "Value": documentData.customerId
                },
                {
                    "Name": "Nombre",
                    "Value": documentData.customerName
                }
            ]
        },
        {
            "ID": "800",
            "DetailsMainField": "Código",
            "Items": [
                { "Name": "Código", "Value": code800 },
                { "Name": "Nombre", "Value": attr800?.description || "" },
                { "Name": "Empresa", "Value": documentData.customerName },
                { "Name": "Cliente", "Value": documentData.customerName },
                { "Name": "FK", "Value": documentData.customerId }
            ]
        },
        ...transformedExistingCenters,  // Agregar los centros existentes
        ...newCenters,       // Agregar los nuevos centros
        ...transformedExistingWorkers,   // Agregar los trabajadores existentes
        ...newWorkers        // Agregar los nuevos trabajadores
    ];

    // Conservar todos los atributos existentes y agregar los nuevos si no existen
    const documentAttributes = [
        {
            "Name": "Cliente",
            "Value": documentData.customerId,
            "ID": "1",
            "Description": documentData.customerName
        },
        {
            "Name": "Título",
            "Value": documentData.title,
            "ID": "3",
            "Description": ""
        },
        {
            // Procesa solo el atributo 800 desde documentData.attributes
            "Name": "Código",
            "Value": code800,
            "ID": "800",
            "Description": attr800?.description || ""
        },
        // Procesar los centros de trabajo existentes (ID 801) y añadirlos
        ...existingCenters.map(center => ({
            "Name": "Centro",
            "Value": center.value,
            "ID": "801",
            "Description": center.description || ""
        })),
        // Procesar los empleados existentes (ID 802) y añadirlos
        ...existingWorkers.map(worker => ({
            "Name": "Trabajador",
            "Value": worker.value,
            "ID": "802",
            "Description": worker.description || ""
        })),
        // Añadir los nuevos centros de trabajo que no existan
        ...uniqueWorkplaceCodes.map(workplaceCode => {
            const userForWorkplace = users.find(user => user.workplaceCode === workplaceCode);

            // Verificar si el centro ya existe
            const existsCentro = existingCenters.some(attr => attr.value === workplaceCode.padStart(5, '0'));
            if (!existsCentro) {
                return {
                    "Name": "Centro",
                    "Value": workplaceCode.padStart(5, '0'),
                    "ID": "801",
                    "Description": userForWorkplace?.workplaceName || ""
                };
            }
        }).filter(Boolean),  // Filtrar los valores nulos
        // Añadir los nuevos empleados que no existan
        ...users.map(user => {
            // Verificar si el empleado ya existe
            const existsWorker = existingWorkers.some(attr => attr.value === user.nif);
            if (!existsWorker) {
                return {
                    "Name": "Trabajador",
                    "Value": user.nif || "",
                    "ID": "802",
                    "Description": `${user.surnames || ""}, ${user.name || ""}`.trim()
                };
            }
        }).filter(Boolean),  // Filtrar los valores nulos
        // Incluir otros atributos que no son 800, 801 o 802
        ...documentData.attributes
            .filter(attr => attr.id !== "800" && attr.id !== "801" && attr.id !== "802") // Excluye 800, 801, 802
            .map(attr => ({
                "Name": attr.name,
                "Value": attr.value,
                "ID": attr.id,
                "Description": attr.description || ""
            }))
    ];


    // Crear el objeto final sin eliminar ni sobrescribir atributos existentes
    let plantillaModificada = {
        "Token": token || null,
        "documentId": documentData.id,
        "Content": base64ToByteArray(documentData.docData) || [],
        "Extension": documentData.type || "",
        "ContainersIds": documentData.containersId || [],
        "isAppDocument": documentData.isAppDoc === 'true' ? true : false,
        ...(documentData.isAppDoc && { "ApplicationGUIDs": ['21DC83DB-B187-4993-8C3E-0B4DF8B15632'] }),
        "DocumentAttributes": documentAttributes,
        "DocumentAttributeValues": documentAttributeValues
    };


    return plantillaModificada;
}
